/**
 * ComplianceTemplateDialog — editor del template "Indice di conformità"
 * (solo OPS): sezioni e voci con rinomina, aggiunta, rimozione e riordino.
 * Le modifiche valgono per i NUOVI controlli (quelli esistenti sono snapshot).
 */
import React, { useEffect, useState } from 'react';
import { complianceApi } from '../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ChevronDown, ChevronUp, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ComplianceTemplateDialog({ onClose }) {
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    complianceApi.template().then(r => setTpl(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento template'); onClose(); });
  }, [onClose]);

  if (!tpl) return null;

  const setSection = (i, patch) => setTpl(t => ({
    ...t, sections: t.sections.map((s, j) => j === i ? { ...s, ...patch } : s),
  }));
  const setItem = (i, k, patch) => setSection(i, {
    items: tpl.sections[i].items.map((it, j) => j === k ? { ...it, ...patch } : it),
  });
  const move = (arr, from, to) => {
    const out = [...arr];
    const [m] = out.splice(from, 1);
    out.splice(to, 0, m);
    return out;
  };

  const save = async () => {
    if (tpl.sections.length === 0) { toast.error('Serve almeno una sezione'); return; }
    if (tpl.sections.some(s => !s.title.trim() || s.items.length === 0 || s.items.some(it => !it.label.trim()))) {
      toast.error('Ogni sezione deve avere un titolo e voci non vuote');
      return;
    }
    setSaving(true);
    try {
      await complianceApi.updateTemplate({
        name: tpl.name || null,
        sections: tpl.sections.map(s => ({
          section_id: s.section_id || null, title: s.title.trim(),
          items: s.items.map(it => ({ item_id: it.item_id || null, label: it.label.trim() })),
        })),
      });
      toast.success('Template conformità salvato');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio fallito');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="compliance-template-dialog">
        <DialogHeader>
          <DialogTitle>Template indice di conformità</DialogTitle>
          <DialogDescription>Le modifiche valgono per i nuovi controlli; quelli già compilati restano invariati.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {tpl.sections.map((sec, i) => (
            <div key={sec.section_id || i} className="border border-slate-200 p-3 space-y-2 bg-slate-50/60"
                 data-testid={`compliance-tpl-section-${i}`}>
              <div className="flex items-center gap-2">
                <div className="flex flex-col shrink-0">
                  <button type="button" disabled={i === 0} className="text-slate-400 hover:text-slate-800 disabled:opacity-20"
                          onClick={() => setTpl(t => ({ ...t, sections: move(t.sections, i, i - 1) }))}
                          data-testid={`compliance-tpl-section-${i}-up`}>
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" disabled={i === tpl.sections.length - 1} className="text-slate-400 hover:text-slate-800 disabled:opacity-20"
                          onClick={() => setTpl(t => ({ ...t, sections: move(t.sections, i, i + 1) }))}
                          data-testid={`compliance-tpl-section-${i}-down`}>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <Input value={sec.title} onChange={e => setSection(i, { title: e.target.value })}
                       placeholder="Titolo sezione" className="h-8 rounded-none font-semibold uppercase text-xs"
                       data-testid={`compliance-tpl-section-${i}-title`} />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0"
                        onClick={() => setTpl(t => ({ ...t, sections: t.sections.filter((_, j) => j !== i) }))}
                        data-testid={`compliance-tpl-section-${i}-remove`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="space-y-1">
                {sec.items.map((it, k) => (
                  <div key={it.item_id || k} className="flex items-center gap-1.5">
                    <div className="flex flex-col shrink-0">
                      <button type="button" disabled={k === 0} className="text-slate-400 hover:text-slate-800 disabled:opacity-20"
                              onClick={() => setSection(i, { items: move(sec.items, k, k - 1) })}
                              data-testid={`compliance-tpl-item-${i}-${k}-up`}>
                        <ChevronUp className="w-3 h-3" />
                      </button>
                      <button type="button" disabled={k === sec.items.length - 1} className="text-slate-400 hover:text-slate-800 disabled:opacity-20"
                              onClick={() => setSection(i, { items: move(sec.items, k, k + 1) })}
                              data-testid={`compliance-tpl-item-${i}-${k}-down`}>
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </div>
                    <Input value={it.label} onChange={e => setItem(i, k, { label: e.target.value })}
                           placeholder="Voce" className="h-8 rounded-none bg-white text-sm"
                           data-testid={`compliance-tpl-item-${i}-${k}-label`} />
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0"
                            onClick={() => setSection(i, { items: sec.items.filter((_, j) => j !== k) })}
                            data-testid={`compliance-tpl-item-${i}-${k}-remove`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="h-7 gap-1 rounded-none text-xs"
                        onClick={() => setSection(i, { items: [...sec.items, { item_id: null, label: '' }] })}
                        data-testid={`compliance-tpl-section-${i}-add-item`}>
                  <Plus className="w-3 h-3" /> Voce
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" className="h-8 gap-1 rounded-none text-xs"
                  onClick={() => setTpl(t => ({ ...t, sections: [...t.sections, { section_id: null, title: '', items: [{ item_id: null, label: '' }] }] }))}
                  data-testid="compliance-tpl-add-section">
            <Plus className="w-3.5 h-3.5" /> Aggiungi sezione
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="compliance-tpl-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
